import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ImageOff, Loader2, Film, CheckCircle2, Circle } from "lucide-react";
import { fmtD } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SharePhotoWall() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [groupBy, setGroupBy] = useState("stage");

  useEffect(() => {
    fetch(`${BACKEND}/api/public/share/${token}`)
      .then((r) => { if (!r.ok) throw new Error("链接无效或已失效"); return r.json(); })
      .then(setData)
      .catch((e) => setErr(e.message));
  }, [token]);

  const fileUrl = (fid) => `${BACKEND}/api/public/share/${token}/file/${fid}`;

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center p-8">
      <ImageOff className="h-12 w-12 text-slate-300 mb-4" />
      <p className="text-slate-500">{err}</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 text-brand-500 animate-spin" /></div>
  );

  const map = new Map();
  (data.media || []).forEach((m) => {
    const key = groupBy === "date" ? (m.date || "—") : (m.phase || "未标注阶段");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  });
  const groups = [...map.entries()];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-500 text-white">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center gap-2 mb-1">
            <img src="/logo192.png" onError={(e) => (e.target.style.display = "none")} alt="" className="h-7 w-7" />
            <span className="text-sm font-semibold tracking-wide">ALIGCUS 阿里建筑 · 项目进度</span>
          </div>
          <h1 className="text-2xl font-bold" data-keep-title>{data.project?.address}</h1>
          <p className="text-white/80 text-sm mt-1">{data.project?.clientName} · {data.project?.type} · {data.project?.phase} · {data.count} 张现场记录</p>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {data.progress && (
          <section className="bg-white rounded-2xl border border-slate-200 p-5 mb-6 shadow-sm" data-testid="share-progress">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-700">项目进度 · {data.progress.stageLabel}</h2>
              <span className="text-lg font-bold text-brand-600">{data.progress.percent}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden mb-4">
              <div className="h-full bg-brand-grad rounded-full transition-all" style={{ width: `${data.progress.percent}%` }} />
            </div>
            <div className="flex flex-wrap gap-2">
              {(data.progress.phases || []).map((p, i) => (
                <div key={i} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${p.done ? "bg-green-50 text-green-700 border-green-200" : p.current ? "bg-red-50 text-red-700 border-red-300 font-semibold" : "bg-slate-50 text-slate-400 border-slate-200"}`}>
                  {p.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : p.current ? <Circle className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5 opacity-40" />}
                  {p.name}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="flex gap-1.5 mb-5">
          {[["stage", "按阶段"], ["date", "按日期"]].map(([k, l]) => (
            <button key={k} data-testid={`share-group-${k}`} onClick={() => setGroupBy(k)}
              className={`text-sm px-3 py-1 rounded-full border ${groupBy === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200"}`}>{l}</button>
          ))}
        </div>

        {(data.media || []).length === 0 && (
          <div className="text-center py-20 text-slate-400"><ImageOff className="h-10 w-10 mx-auto mb-3 text-slate-300" />暂无现场照片</div>
        )}

        {groups.map(([label, items]) => (
          <section key={label} className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <h2 className="text-base font-semibold text-brand-600">{label}</h2>
              <span className="text-xs text-slate-400">· {items.length}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((m) => (
                <a key={m.fileId} href={fileUrl(m.fileId)} target="_blank" rel="noreferrer"
                  className="relative block rounded-lg overflow-hidden border border-slate-200 bg-white aspect-square group shadow-sm hover:shadow-md transition-shadow">
                  {m.kind === "image"
                    ? <img src={fileUrl(m.fileId)} alt={m.filename} className="w-full h-full object-cover" />
                    : <span className="w-full h-full flex items-center justify-center bg-black"><Film className="h-7 w-7 text-white/80" /></span>}
                  <span className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[10px] px-1.5 py-1 truncate">{fmtD(m.date)}{m.caption ? " · " + m.caption : ""}</span>
                </a>
              ))}
            </div>
          </section>
        ))}

        <p className="text-center text-xs text-slate-400 mt-10">本页面由阿里建筑项目管理系统自动生成 · 仅供业主查看现场进度</p>
      </div>
    </div>
  );
}
